// Medication refill capture — pure, Vue-free logic behind RefillPicker.vue,
// unit-tested directly (frontend/test/checkup/MedicationsStep.spec.js).
//
// The #656 feedback round replaced the derived dosing model (how often do you
// take it × how much do you pick up, folded together by the old
// deriveQuote()) with Dien's refill format: the client states EACH REFILL
// directly — a quantity plus how often it's picked up (every 1/2/3/6/12
// months) — because that's what the pharmacy label actually says. The pair
// maps 1:1 onto the stored Drug shape's `quantity` +
// `frequency` ('FREQUENCY_{days}_DAYS', flow/consults/serializers.py) with
// no derivation, which also makes editing a saved row lossless
// (refillFromStored()).
//
// "Taken only as needed" is a flag on the same answer (stored as
// Drug.as_needed): the frequency options reword to "refilled about every…"
// because the client is estimating a pace rather than reporting a schedule.
// Its extra option, "rarely: keep on file, leave out of estimates", maps to
// the existing Drug.excluded flag — exactly the field the quote path already
// filters on (flow/consults/endpoints/quotes.py drops excluded drugs before
// Oracle), parked on the longest frequency so the stored pair stays valid.

export const REFILL_MONTHS = [1, 2, 3, 6, 12]

/** A fresh refill answer: one month's pickup, defaulted from the selected
 *  package's 30-day quantity when one is known. */
export function defaultRefill (pkg) {
    const qty30 = pkg && Number(pkg.default_quantity_30)
    return {
        quantity: Number.isFinite(qty30) && qty30 > 0 ? qty30 : 30,
        months: 1,
        asNeeded: false,
    }
}

/** The day count inside a stored Drug.frequency ('FREQUENCY_90_DAYS' -> 90),
 *  or null for anything unrecognized. */
export function frequencyDays (frequency) {
    const match = /^FREQUENCY_(\d+)_DAYS$/.exec(frequency || '')
    return match ? Number(match[1]) : null
}

/** The stored fields for a refill answer. Quantity is clamped to a positive
 *  integer (the serializer requires conint ge=1); 'rarely' becomes
 *  excluded=true on the longest frequency. `excluded` is always an explicit
 *  boolean so editing a drug OFF the rarely option clears the flag. */
export function refillToStored (refill) {
    const rarely = refill.months === 'rarely'
    const months = rarely ? 12 : (REFILL_MONTHS.includes(refill.months) ? refill.months : 1)
    return {
        quantity: Math.max(1, Math.round(Number(refill.quantity)) || 1),
        frequency: `FREQUENCY_${months * 30}_DAYS`,
        as_needed: !!refill.asNeeded,
        excluded: rarely,
    }
}

/** The refill answer a stored Drug represents — the read direction of
 *  refillToStored(). Rows saved before the refill format (or by an agent)
 *  carry quantity + frequency only, so they land on sensible defaults:
 *  their real quantity, the nearest month option, not as-needed. */
export function refillFromStored (drug) {
    const days = frequencyDays(drug && drug.frequency)
    const months = days && REFILL_MONTHS.includes(Math.round(days / 30)) ? Math.round(days / 30) : 1
    return {
        quantity: (drug && drug.quantity) || 30,
        months: drug && drug.excluded ? 'rarely' : months,
        asNeeded: !!(drug && drug.as_needed),
    }
}

/** Row/pricing phrase for a refill answer, e.g. "30 tablets every month" or
 *  "12 capsules about every 3 months". */
export function refillLabel (refill, unit = 'units') {
    if (refill.months === 'rarely') { return 'kept on file, not in estimates' }
    const period = refill.months === 1 ? 'month' : `${refill.months} months`
    const qty = Math.max(1, Math.round(Number(refill.quantity)) || 1)
    return `${qty} ${unit} ${refill.asNeeded ? 'about every' : 'every'} ${period}`
}

/** The counting word for a dosage form ("tablets," between the quantity input
 *  and the frequency select). Works on Oracle's raw all-caps forms
 *  ('CAPSULE_DELAYED_RELEASE') and the manual form labels ('Tablet / Pill'). */
export function unitWordForForm (form) {
    const f = (form || '').toUpperCase()
    if (f.includes('TABLET') || f.includes('PILL')) { return 'tablets' }
    if (f.includes('CAPSULE')) { return 'capsules' }
    if (f.includes('PATCH')) { return 'patches' }
    if (f.includes('INHAL')) { return 'inhalers' }
    return 'units'
}

/** Row label for a LEGACY days-supply value, e.g. "90-day fills" — kept for
 *  rows saved before the refill format, whose custom_dosage is absent and
 *  whose frequency is the only trace of their fill size (see
 *  MedicationsStep.drugSub()). */
export function daysSupplyLabel (daysSupply) {
    return daysSupply === 30 || daysSupply === 60 || daysSupply === 90 ? `${daysSupply}-day fills` : ''
}

/** The days-supply implied by a stored legacy Drug.frequency
 *  ('FREQUENCY_30_DAYS' -> 30); null for anything unrecognized. */
export function daysSupplyFromFrequency (frequency) {
    const days = frequencyDays(frequency)
    return days === 30 || days === 60 || days === 90 ? days : null
}

/**
 * Strength + units as a single display string, e.g. "20 mg" — flow splits
 * DrugDosage.strength ("20") and .strength_units ("MG") into two fields
 * (flow/consults/serializers.py:25-31); the platform's own DrugDosage ships
 * them pre-combined ("10 mg", or "20.6 (20 Base) mg" for compound
 * strengths). Empty when the dosage has no strength at all — callers decide
 * their own fallback (the dosage option falls back to the form; the
 * saved-row name/custom_dosage just omit it).
 */
export function dosageStrength (dosage) {
    if (!dosage || !dosage.strength) { return '' }
    return [dosage.strength, (dosage.strength_units || '').toLowerCase()].filter(Boolean).join(' ')
}

/** Title-cases a raw, often all-caps/underscored Oracle form string, e.g.
 *  "CAPSULE_DELAYED_RELEASE" -> "Capsule Delayed Release". */
export function titleCase (s) {
    return (s || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Sorts a getDrugOptions dosage list ascending by numeric strength (platform
 * parity, browser-QA finding: Oracle returns dosages in an arbitrary raw
 * order — e.g. 40/20/10/20.6 — but the platform always shows them ascending
 * and preselects the lowest). `parseFloat` reads the leading number off a
 * dosage's raw strength string (e.g. "20.6 (20 Base)" -> 20.6), so compound
 * strengths sort by their primary value. Dosages with no parseable numeric
 * strength keep their original relative order, stably pushed after every
 * numeric one — deliberately explicit about tie-breaking (by original
 * index) rather than relying on the sort being stable, since the comparator
 * itself branches on which side (if either) is non-numeric.
 */
export function sortDosagesByStrength (dosages) {
    return (dosages || [])
        .map((d, i) => ({ d, i, n: parseFloat(d && d.strength) }))
        .sort((a, b) => {
            const aNum = Number.isFinite(a.n)
            const bNum = Number.isFinite(b.n)
            if (!aNum && !bNum) { return a.i - b.i }
            if (!aNum) { return 1 }
            if (!bNum) { return -1 }
            return a.n - b.n
        })
        .map(x => x.d)
}
